import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaCamera,
  FaCheckCircle,
  FaClock,
  FaGlobeAmericas,
  FaImage,
  FaLock,
  FaMapMarkerAlt,
  FaTimes,
  FaUpload,
  FaVideo
} from 'react-icons/fa';
import '../css/CreateMoment.css';

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const MAX_VIDEO_SIZE = 50 * 1024 * 1024;
const MAX_VIDEO_DURATION = 30;
const MAX_CAPTION_LENGTH = 1000;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];

function CreateMoment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const previewUrlRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [mediaType, setMediaType] = useState('');
  const [durationSeconds, setDurationSeconds] = useState(null);
  const [caption, setCaption] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [commentsEnabled, setCommentsEnabled] = useState(true);
  const [privateMoment, setPrivateMoment] = useState(false);
  const [expirationHours, setExpirationHours] = useState(24);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [locationMessage, setLocationMessage] = useState('');

  const token = localStorage.getItem('authToken');

  const authorizationHeader = useMemo(() => {
    if (!token) return null;
    return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }, [token]);

  const currentUser = useMemo(() => {
    try {
      const stored = localStorage.getItem('currentUser');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    document.body.classList.add('create-moment-body');

    if (currentUser) {
      setCity(currentUser.city || '');
      setState(currentUser.state || '');
      setCountry(currentUser.country || '');
    }

    return () => {
      document.body.classList.remove('create-moment-body');
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, [currentUser]);

  const resetSelectedMedia = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setSelectedFile(null);
    setPreviewUrl('');
    setMediaType('');
    setDurationSeconds(null);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validateFile = (file) => {
    if (!file) throw new Error(t('moments.fileRequired', 'Choisissez une photo ou une vidéo.'));

    const isImage = ALLOWED_IMAGE_TYPES.includes(file.type);
    const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type);

    if (!isImage && !isVideo) {
      throw new Error(t('moments.invalidFileType', 'Format non accepté. Utilisez JPG, PNG, WEBP, MP4, WEBM ou MOV.'));
    }

    if (isImage && file.size > MAX_IMAGE_SIZE) {
      throw new Error(t('moments.imageTooLarge', 'La photo ne peut pas dépasser 10 MB.'));
    }

    if (isVideo && file.size > MAX_VIDEO_SIZE) {
      throw new Error(t('moments.videoTooLarge', 'La vidéo ne peut pas dépasser 50 MB.'));
    }

    return isVideo ? 'VIDEO' : 'PHOTO';
  };

  const readVideoDuration = (file) => new Promise((resolve, reject) => {
    const temporaryUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      const duration = Math.ceil(video.duration);
      URL.revokeObjectURL(temporaryUrl);
      if (!Number.isFinite(duration) || duration <= 0) {
        reject(new Error(t('moments.durationReadError', 'Impossible de lire la durée de cette vidéo.')));
        return;
      }
      resolve(duration);
    };

    video.onerror = () => {
      URL.revokeObjectURL(temporaryUrl);
      reject(new Error(t('moments.videoReadError', 'Impossible de lire cette vidéo.')));
    };

    video.src = temporaryUrl;
  });

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const detectedMediaType = validateFile(file);
      let detectedDuration = null;

      if (detectedMediaType === 'VIDEO') {
        detectedDuration = await readVideoDuration(file);
        if (detectedDuration > MAX_VIDEO_DURATION) {
          throw new Error(t('moments.videoDurationError', `La vidéo ne peut pas dépasser ${MAX_VIDEO_DURATION} secondes.`));
        }
      }

      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      const objectUrl = URL.createObjectURL(file);
      previewUrlRef.current = objectUrl;
      setSelectedFile(file);
      setPreviewUrl(objectUrl);
      setMediaType(detectedMediaType);
      setDurationSeconds(detectedDuration);
    } catch (error) {
      resetSelectedMedia();
      setErrorMessage(error.message);
    }
  };

  const reverseGeocode = async (latitude, longitude) => {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&lat=${latitude}&lon=${longitude}`
    );
    if (!response.ok) throw new Error(`Reverse geocoding failed: ${response.status}`);
    const data = await response.json();
    const address = data.address || {};
    return {
      city: address.city || address.town || address.village || address.suburb || '',
      state: address.state || address.region || '',
      country: address.country || ''
    };
  };

  const detectLocation = () => {
    if (!navigator.geolocation) {
      setLocationMessage(t('moments.geolocationUnsupported', 'La géolocalisation n’est pas disponible sur cet appareil.'));
      return;
    }

    setLocationLoading(true);
    setLocationMessage('');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const location = await reverseGeocode(position.coords.latitude, position.coords.longitude);
          setCity(location.city);
          setState(location.state);
          setCountry(location.country);
          setLocationMessage(t('moments.locationDetected', 'Localisation trouvée.'));
        } catch (error) {
          console.error('Moment reverse geocoding failed:', error);
          setLocationMessage(t('moments.locationAddressError', 'Impossible de déterminer la ville. Vous pouvez la saisir manuellement.'));
        } finally {
          setLocationLoading(false);
        }
      },
      () => {
        setLocationLoading(false);
        setLocationMessage(t('moments.locationPermissionError', 'Autorisez la localisation ou entrez votre ville manuellement.'));
      },
      { enableHighAccuracy: false, timeout: 30000, maximumAge: 600000 }
    );
  };

  const validateForm = () => {
    if (!authorizationHeader) {
      setErrorMessage(t('moments.authenticationRequired', 'Vous devez être connecté pour publier un Moment.'));
      return false;
    }
    if (!selectedFile || !mediaType) {
      setErrorMessage(t('moments.fileRequired', 'Choisissez une photo ou une vidéo.'));
      return false;
    }
    if (caption.trim().length > MAX_CAPTION_LENGTH) {
      setErrorMessage(t('moments.captionTooLong', 'Le texte ne peut pas dépasser 1000 caractères.'));
      return false;
    }
    if (mediaType === 'VIDEO' && (!durationSeconds || durationSeconds > MAX_VIDEO_DURATION)) {
      setErrorMessage(t('moments.videoDurationError', `La vidéo ne peut pas dépasser ${MAX_VIDEO_DURATION} secondes.`));
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const formData = new FormData();
    formData.append('media', selectedFile);
    if (caption.trim()) formData.append('caption', caption.trim());
    if (durationSeconds) formData.append('durationSeconds', String(durationSeconds));
    if (city.trim()) formData.append('city', city.trim());
    if (state.trim()) formData.append('state', state.trim());
    if (country.trim()) formData.append('country', country.trim());
    formData.append('commentsEnabled', String(commentsEnabled));
    formData.append('privateMoment', String(privateMoment));
    formData.append('expirationHours', String(expirationHours));

    setPublishing(true);
    setUploadProgress(0);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/moments`, formData, {
        headers: {
          Authorization: authorizationHeader,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      });

      setSuccessMessage(t('moments.publishSuccess', 'Votre Moment a été publié avec succès.'));
      window.dispatchEvent(new Event('refresh-moments'));
      window.setTimeout(() => navigate('/moments'), 1100);
    } catch (error) {
      console.error('Create Moment failed:', error);
      setErrorMessage(error.response?.data?.message || t('moments.publishError', 'Impossible de publier ce Moment.'));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <main className="create-moment-page">
      <section className="create-moment-container">
        <header className="create-moment-header">
          <button type="button" className="create-moment-back" onClick={() => navigate(-1)} aria-label={t('common.back', 'Retour')}>
            <FaArrowLeft />
          </button>
          <div>
            <h1>{t('moments.createTitle', 'Créer un Moment')}</h1>
            <p>{t('moments.createSubtitle', 'Partagez une photo ou une courte vidéo.')}</p>
          </div>
        </header>

        <form className="create-moment-form" onSubmit={handleSubmit}>
          {errorMessage && <div className="create-moment-message create-moment-message--error" role="alert">{errorMessage}</div>}
          {successMessage && <div className="create-moment-message create-moment-message--success" role="status"><FaCheckCircle />{successMessage}</div>}

          <section className="create-moment-section">
            <div className="create-moment-section-title"><span>01</span><div><h2>{t('moments.mediaSection', 'Photo ou vidéo')}</h2><p>{t('moments.mediaHint', 'Vidéo de 30 secondes maximum.')}</p></div></div>

            {!previewUrl ? (
              <button type="button" className="create-moment-dropzone" onClick={() => fileInputRef.current?.click()}>
                <span className="create-moment-dropzone-icon"><FaCamera /></span>
                <strong>{t('moments.chooseMedia', 'Choisir une photo ou une vidéo')}</strong>
                <small>{t('moments.acceptedFormats', 'JPG, PNG, WEBP, MP4, WEBM ou MOV')}</small>
              </button>
            ) : (
              <div className="create-moment-preview">
                {mediaType === 'VIDEO' ? <video src={previewUrl} controls playsInline preload="metadata" /> : <img src={previewUrl} alt={t('moments.preview', 'Aperçu')} />}
                <button type="button" className="create-moment-remove-media" onClick={resetSelectedMedia} aria-label={t('moments.removeMedia', 'Retirer le média')}><FaTimes /></button>
                <span className="create-moment-media-badge">
                  {mediaType === 'VIDEO' ? <><FaVideo />{durationSeconds}s</> : <><FaImage />{t('moments.photo', 'Photo')}</>}
                </span>
              </div>
            )}

            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime" hidden onChange={handleFileChange} />
          </section>

          <section className="create-moment-section">
            <div className="create-moment-section-title"><span>02</span><div><h2>{t('moments.captionSection', 'Parlez de ce Moment')}</h2><p>{t('moments.captionHint', 'Ajoutez un petit texte pour donner du contexte.')}</p></div></div>
            <div className="create-moment-caption">
              <textarea value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={MAX_CAPTION_LENGTH} rows="5" placeholder={t('moments.captionPlaceholder', 'Que souhaitez-vous partager ?')} />
              <span className={caption.length > 900 ? 'is-near-limit' : ''}>{caption.length}/{MAX_CAPTION_LENGTH}</span>
            </div>
          </section>

          <section className="create-moment-section">
            <div className="create-moment-section-title"><span>03</span><div><h2>{t('moments.locationSection', 'Localisation')}</h2><p>{t('moments.locationHint', 'La ville est facultative. Ne partagez pas votre adresse exacte.')}</p></div></div>
            <button type="button" className="create-moment-detect-location" onClick={detectLocation} disabled={locationLoading}><FaMapMarkerAlt />{locationLoading ? t('moments.detectingLocation', 'Détection...') : t('moments.detectLocation', 'Utiliser ma localisation')}</button>
            {locationMessage && <p className="create-moment-location-message">{locationMessage}</p>}
            <div className="create-moment-location-grid">
              <label><span>{t('moments.city', 'Ville')}</span><input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Bronx" /></label>
              <label><span>{t('moments.state', 'État')}</span><input type="text" value={state} onChange={(e) => setState(e.target.value)} placeholder="New York" /></label>
              <label><span>{t('moments.country', 'Pays')}</span><input type="text" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="United States" /></label>
            </div>
          </section>

          <section className="create-moment-section">
            <div className="create-moment-section-title"><span>04</span><div><h2>{t('moments.settingsSection', 'Options')}</h2><p>{t('moments.settingsHint', 'Choisissez la durée et la visibilité.')}</p></div></div>
            <div className="create-moment-option-list">
              <label className="create-moment-option"><span className="create-moment-option-icon"><FaClock /></span><span className="create-moment-option-copy"><strong>{t('moments.expiration', 'Expiration')}</strong><small>{t('moments.expirationHint', 'Le Moment disparaîtra automatiquement.')}</small></span><select value={expirationHours} onChange={(e) => setExpirationHours(Number(e.target.value))}><option value={24}>24h</option><option value={48}>48h</option></select></label>
              <label className="create-moment-option"><span className="create-moment-option-icon"><FaGlobeAmericas /></span><span className="create-moment-option-copy"><strong>{t('moments.comments', 'Commentaires')}</strong><small>{t('moments.commentsHint', 'Autoriser les autres à commenter.')}</small></span><input type="checkbox" checked={commentsEnabled} onChange={(e) => setCommentsEnabled(e.target.checked)} /><i /></label>
              <label className="create-moment-option"><span className="create-moment-option-icon"><FaLock /></span><span className="create-moment-option-copy"><strong>{t('moments.privateMoment', 'Moment privé')}</strong><small>{t('moments.privateMomentHint', 'Réservé aux personnes autorisées selon vos règles.')}</small></span><input type="checkbox" checked={privateMoment} onChange={(e) => setPrivateMoment(e.target.checked)} /><i /></label>
            </div>
          </section>

          {publishing && <div className="create-moment-progress"><div><span style={{ width: `${uploadProgress}%` }} /></div><p>{t('moments.uploading', 'Publication en cours...')} {uploadProgress}%</p></div>}

          <button type="submit" className="create-moment-submit" disabled={publishing}><FaUpload />{publishing ? t('moments.publishing', 'Publication...') : t('moments.publish', 'Publier le Moment')}</button>
        </form>
      </section>
    </main>
  );
}

export default CreateMoment;